// src/components/User/Products/ProductCard.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { products } from './ProductData';

const ProductCard = () => {
    const navigate = useNavigate();
  // Group products by category for better organization
  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) acc[product.category] = [];
    acc[product.category].push(product);
    return acc;
  }, {});

  return (
    <>
  {/* Hero Section */}
  <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12 text-center">
    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Product Suite</h1>
    <p className="text-xl lg:text-2xl text-gray-700 mb-4 max-w-3xl mx-auto">
      Powerful Products for Modern Supply Chains
    </p>
    <p className="text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
      Six integrated capability pillars — each module operates independently, 
      yet performs exponentially better together.
    </p>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
      <Link to="/contact" className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-yellow-500 rounded-lg hover:shadow-md transition-all">
        Schedule a Demo
      </Link>
      <Link to="/industries" className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all">
        Explore Industries →
      </Link>
    </div>
  </section>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
        <div key={category} className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-l-4 border-orange-500 pl-3">
            {category}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryProducts.map((product) => {
              const IconComponent = Icons[product.icon] || Icons.Package;
              const topMetrics = product.metrics?.slice(0, 2) || [];

              return (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="block group"
                >
                  <div className="bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-gray-100 h-full flex flex-col">
                    {/* Orange/yellow gradient header */}
                    <div className="h-2 bg-gradient-to-r from-orange-500 to-yellow-400"></div>
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Category badge */}
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-orange-50 text-orange-600">
                          {product.category}
                        </span>
                        {product.badge && (
                          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                            {product.badge}
                          </span>
                        )}
                      </div>

                      {/* Icon and title */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-orange-100 text-orange-600">
                          <IconComponent size={24} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800 group-hover:text-orange-600 transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-sm text-gray-500">{product.tagline}</p>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                        {product.description}
                      </p>

                      {/* Metrics */}
                      {topMetrics.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {topMetrics.map((metric, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-2 text-center">
                              <div className="text-lg font-bold text-orange-600">{metric.value}</div>
                              <div className="text-xs text-gray-500">{metric.label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Button */}
                      <div className="mt-auto pt-3">
                        <span className="inline-flex items-center justify-center w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 rounded-xl text-sm font-semibold transition-all hover:from-orange-600 hover:to-yellow-600">
                          Explore {product.name}
                          <Icons.ArrowRight className="ml-2 w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
    </>
  );
};

export default ProductCard;